import { Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import SnackbarUtils from "../../../../utils/snackbarUtils";
import { StyledDropzone } from "../../../styled/dropzone";
import FileTypeDialog from "./fileTypeDialog";
import UploadedFiles from "./uploadedFiles";

const DropZone = ({
  files,
  setFiles,
  configuration,
  error,
  setError,
  disabled,
  selectedDocument,
  accept,
  dropzoneText,
  name,
}) => {
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [formatAvailableStrings, setFormatAvailableStrings] = useState([]);
  const [isEditingFile, setIsEditingFile] = useState(false);

  const fileMinSizeInBytes = configuration.fileMinSizeInBytes || 1024;
  const useBrowserFileSystemAPI = false;

  useEffect(() => {
    const extensiones = Object.values(accept).reduce(
      (acumulador, valorActual) => acumulador.concat(valorActual),
      [],
    );
    setFormatAvailableStrings(extensiones);
  }, [accept]);

  const handleCloseTypeDialog = () => {
    setOpenTypeDialog(false);
    setSelectedFile({});
    setIsEditingFile(false);
  };

  const handleSubmitTypeDialog = (fileTipificado) => {
    let fileToUpdate = files.find((f) => f.nombre === fileTipificado.filename);
    if (fileToUpdate) {
      Object.assign(fileToUpdate, fileTipificado);
    } else {
      files.push(fileTipificado);
      setError(false);
    }
    handleCloseTypeDialog();
  };

  const validateFile = (file) => {
    const allowedImageFormats = accept["image/*"];
    const indexExtension = file.name?.lastIndexOf(".");
    const extension = file.name?.slice(indexExtension).toLowerCase();
    const esImagen = file.type?.split("/")[0] === "image";
    if (
      allowedImageFormats &&
      !allowedImageFormats.includes(extension) &&
      esImagen
    ) {
      return { code: "file-invalid-type" };
    } else {
      return false;
    }
  };

  const onDropAccepted = (file) => {
    let fileArray = files ?? [];
    const reader = new FileReader();

    if (configuration?.oneFile) {
      const f = file[0];
      reader.readAsDataURL(f);
      reader.onload = function () {
        const index = reader.result.indexOf("base64,") + 7;
        fileArray = fileArray.toSpliced(0, 1, {
          filename: f.name,
          value: reader.result.substring(index),
          base64Header: reader.result.substring(0, index),
          mimeType: f.type,
          bytes: f.size,
        });
        setFiles(fileArray);
        setSelectedFile(f);
        if (configuration.typifyAttachmentFile) {
          setOpenTypeDialog(true);
        }
      };
      reader.onerror = function () {
        SnackbarUtils.error("Archivo inválido");
      };
    } else {
      file.forEach((f) => {
        reader.readAsDataURL(f);
        reader.onload = function () {
          const index = reader.result.indexOf("base64,") + 7;
          const archivoExistente = fileArray.find((a) => a.filename === f.name);
          if (archivoExistente) {
            fileArray = fileArray.toSpliced(0, 1, {
              nombre: f.name,
              value: reader.result.substring(index),
              base64Header: reader.result.substring(0, index),
              mimeType: f.type,
              bytes: f.size,
            });
          } else {
            fileArray.push({
              nombre: f.name,
              value: reader.result.substring(index),
              base64Header: reader.result.substring(0, index),
              mimeType: f.type,
              bytes: f.size,
            });
          }
          setFiles(fileArray);
          setSelectedFile(f);
          if (configuration.typifyAttachmentFile) {
            setOpenTypeDialog(true);
          }
        };
        reader.onerror = function () {
          SnackbarUtils.error("Archivo inválido");
        };
      });
    }
  };

  const onDropRejected = (error) => {
    error[0].errors.forEach((error) => {
      switch (error.code) {
        case "file-too-large":
          SnackbarUtils.error(
            `El archivo debe pesar menos de ${configuration.maxFileSizeBytes} bytes`,
          );
          break;
        case "file-invalid-type":
          SnackbarUtils.error(
            `El archivo debe ser ${formatAvailableStrings.toString()}`,
          );
          break;
        case "file-too-small":
          SnackbarUtils.error(
            `El archivo debe pesar más de ${fileMinSizeInBytes} bytes`,
          );
          break;
        default:
          SnackbarUtils.error("Archivo no permitido");
          break;
      }
    });
  };

  const handleEditFile = (file) => {
    setIsEditingFile(true);
    setSelectedFile(file);
    setOpenTypeDialog(true);
  };

  const handleDeleteFile = (file) => {
    setFiles(files.filter((f) => f.nombre !== file.nombre));
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDropAccepted,
    onDropRejected,
    useFsAccessApi: useBrowserFileSystemAPI,
    accept: accept,
    validator: validateFile,
    multiple: configuration.oneFile ? false : true,
    maxSize: configuration.maxFileSizeBytes,
    minSize: fileMinSizeInBytes,
    disabled: disabled,
  });

  return (
    <StyledDropzone.Container>
      <StyledDropzone.DropZoneBase
        {...getRootProps()}
        isActive={isDragActive}
        isAccept={isDragAccept}
        isReject={isDragReject}
        name={name || "attachment"}>
        <input {...getInputProps()} />
        <Typography variant="subtitle1" align="center">
          {dropzoneText ??
            "Arrastre o haga click en el recuadro para adjuntar archivos"}
        </Typography>
      </StyledDropzone.DropZoneBase>
      <UploadedFiles
        files={files}
        handleEdit={handleEditFile}
        handleDeleteFile={handleDeleteFile}
        configuration={configuration}
        selectedDocument={selectedDocument}
        disabled={disabled}
      />
      <FileTypeDialog
        file={selectedFile}
        open={openTypeDialog}
        handleClose={handleCloseTypeDialog}
        handleSubmit={handleSubmitTypeDialog}
        configuration={configuration}
        isEditingFile={isEditingFile}
        deleteLastFile={() =>
          setFiles(files.filter((e) => e.nombre !== selectedFile.name))
        }
      />
      {error && (
        <Typography color="error">Debe adjuntar al menos un archivo</Typography>
      )}
    </StyledDropzone.Container>
  );
};

export default DropZone;
